import React from 'react'
import styled from 'styled-components'
import DocumentTitle from 'react-document-title'
import {
    isSupabaseConfigured,
    fetchTournaments,
    searchTournaments,
    fetchMatchesByTournament,
} from '../../lib/supabaseApi'

const Page = styled.div`
    max-width: 900px;
    margin: 0 auto;
    padding: 2rem 0;
`

const Subtitle = styled.p`
    font-size: 1rem;
    color: var(--grey-1);
    margin-bottom: 2.5rem;
`

const Section = styled.section`
    margin-bottom: 2.5rem;
`

const SectionTitle = styled.h2`
    font-size: 1.35rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: var(--grey-2);
`

const SearchInput = styled.input`
    width: 100%;
    max-width: 400px;
    margin-bottom: 1rem;
    padding: 0.65rem 1rem;
    font-size: 1rem;
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--grey-2);
    &::placeholder {
        color: var(--grey-1);
    }
`

const List = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--bg-elevated);
    overflow: hidden;
`

const ListItem = styled.li`
    padding: 0.9rem 1rem;
    border-bottom: 1px solid var(--border);
    cursor: pointer;
    transition: background 0.15s;
    &:last-child {
        border-bottom: none;
    }
    &:hover {
        background: var(--grey-0);
    }
`

const MatchLink = styled.a`
    display: block;
    color: var(--grey-2);
    text-decoration: none;
    &:hover {
        color: var(--accent);
    }
`

const MatchTitle = styled.span`
    font-weight: 500;
`

const Empty = styled.p`
    color: var(--grey-1);
    padding: 1.5rem;
    margin: 0;
`

const BackButton = styled.button`
    margin-bottom: 1rem;
    background: transparent;
    border: 1px solid var(--border);
    color: var(--grey-2);
    padding: 0.5rem 1rem;
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.9rem;
    &:hover {
        background: var(--grey-0);
    }
`

const DaySelectorList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 1rem;
`

const DaySelectorItem = styled.button`
    width: 100%;
    text-align: left;
    padding: 0.85rem 1rem;
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--grey-2);
    font-size: 1rem;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
    &:hover {
        background: var(--grey-0);
        border-color: var(--grey-1);
    }
`

const DaySelectorDate = styled.span`
    font-weight: 600;
    display: block;
    margin-bottom: 2px;
`

const DaySelectorCount = styled.span`
    font-size: 0.85rem;
    color: var(--grey-1);
`

function formatMatchDate(isoDate) {
    if (!isoDate) return ''
    const d = String(isoDate).slice(0, 10).split('-')
    if (d.length !== 3) return isoDate
    return `${d[2]}.${d[1]}.${d[0]}`
}

function formatUploadTime(createdAt) {
    if (!createdAt) return ''
    const d = new Date(createdAt)
    const date = formatMatchDate(d.toISOString().slice(0, 10))
    const time = d.toTimeString().slice(0, 5)
    return date + ' ' + time
}

function getMatchLabel(m) {
    const mapLabel = (m.map_name && m.map_name.trim()) ? m.map_name.trim() : ''
    const title = (m.title && m.title.trim()) ? m.title.trim() : ''
    const uploadTime = formatUploadTime(m.created_at)
    const parts = []
    if (mapLabel) parts.push(mapLabel)
    if (title) parts.push(title)
    if (uploadTime) parts.push(uploadTime)
    if (parts.length) return parts.join(' — ')
    return m.title || `Maç ${m.id}`
}

class Home extends React.Component {
    state = {
        search: '',
        tournaments: [],
        tournamentsLoading: true,
        selectedTournament: null,
        matches: [],
        matchesLoading: false,
        selectedDateKey: null,
    }

    componentDidMount() {
        if (isSupabaseConfigured()) {
            this.loadTournaments()
        } else {
            this.setState({ tournamentsLoading: false })
        }
    }

    loadTournaments = async () => {
        this.setState({ tournamentsLoading: true })
        const list = await fetchTournaments()
        this.setState({ tournaments: list || [], tournamentsLoading: false })
    }

    handleSearch = async e => {
        const q = e.target.value
        this.setState({ search: q })
        if (!isSupabaseConfigured()) return
        if (!q.trim()) {
            const list = await fetchTournaments()
            this.setState({ tournaments: list || [] })
            return
        }
        const list = await searchTournaments(q)
        this.setState({ tournaments: list || [] })
    }

    selectTournament = async t => {
        if (!t || !t.id) return
        this.setState({
            selectedTournament: t,
            matchesLoading: true,
            matches: [],
            selectedDateKey: null,
        })
        const list = await fetchMatchesByTournament(t.id)
        this.setState({ matches: list || [], matchesLoading: false })
    }

    clearTournament = () => {
        this.setState({
            selectedTournament: null,
            matches: [],
            selectedDateKey: null,
        })
    }

    clearSelectedDay = () => {
        this.setState({ selectedDateKey: null })
    }

    render() {
        const {
            search,
            tournaments,
            tournamentsLoading,
            selectedTournament,
            matches,
            matchesLoading,
        } = this.state
        const showSupabase = isSupabaseConfigured()

        return (
            <Page>
                <DocumentTitle title="DropScore — Turnuva Maçları" />
                <Subtitle>
                    Kayıtlı turnuva maçlarını arayıp izleyin.
                </Subtitle>

                {showSupabase && (
                    <Section>
                        <SectionTitle>Turnuva maçları</SectionTitle>
                        {!selectedTournament ? (
                            <React.Fragment>
                                <SearchInput
                                    type="text"
                                    placeholder="Turnuva adına göre ara..."
                                    value={search}
                                    onChange={this.handleSearch}
                                />
                                <List>
                                    {tournamentsLoading && (
                                        <Empty>Yükleniyor...</Empty>
                                    )}
                                    {!tournamentsLoading && tournaments.length === 0 && (
                                        <Empty>Turnuva bulunamadı.</Empty>
                                    )}
                                    {!tournamentsLoading &&
                                        tournaments.map(t => (
                                            <ListItem
                                                key={t.id}
                                                onClick={() => this.selectTournament(t)}
                                            >
                                                {t.name || t.title || `Turnuva ${t.id}`}
                                            </ListItem>
                                        ))}
                                </List>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <BackButton type="button" onClick={this.clearTournament}>
                                    ← Turnuvalara dön
                                </BackButton>
                                <SectionTitle>
                                    {selectedTournament.name ||
                                        selectedTournament.title ||
                                        'Maçlar'}
                                </SectionTitle>
                                {matchesLoading && (
                                    <Empty>Maçlar yükleniyor...</Empty>
                                )}
                                {!matchesLoading && matches.length === 0 && (
                                    <Empty>Bu turnuvada maç kaydı yok.</Empty>
                                )}
                                {!matchesLoading && matches.length > 0 && (() => {
                                    const byUploadDate = {}
                                    matches.forEach(m => {
                                        const d = m.created_at || ''
                                        const key = String(d).slice(0, 10) || 'tarihsiz'
                                        if (!byUploadDate[key]) byUploadDate[key] = []
                                        byUploadDate[key].push(m)
                                    })
                                    const dateKeys = Object.keys(byUploadDate).filter(k => k !== 'tarihsiz').sort().reverse()
                                    const hasTarihsiz = byUploadDate.tarihsiz && byUploadDate.tarihsiz.length > 0
                                    if (this.state.selectedDateKey === null) {
                                        return (
                                            <React.Fragment>
                                                <Subtitle style={{ marginBottom: '1rem' }}>
                                                    Hangi günün maçlarını görmek istiyorsunuz? Günü seçin, o güne ait tüm maçlar listelenecektir.
                                                </Subtitle>
                                                <DaySelectorList>
                                                    {dateKeys.map(dateKey => (
                                                        <DaySelectorItem
                                                            key={dateKey}
                                                            type="button"
                                                            onClick={() => this.setState({ selectedDateKey: dateKey })}
                                                        >
                                                            <DaySelectorDate>{formatMatchDate(dateKey)}</DaySelectorDate>
                                                            <DaySelectorCount>{byUploadDate[dateKey].length} maç</DaySelectorCount>
                                                        </DaySelectorItem>
                                                    ))}
                                                    {hasTarihsiz && (
                                                        <DaySelectorItem
                                                            type="button"
                                                            onClick={() => this.setState({ selectedDateKey: 'tarihsiz' })}
                                                        >
                                                            <DaySelectorDate>Yükleme tarihi belirtilmemiş</DaySelectorDate>
                                                            <DaySelectorCount>{byUploadDate.tarihsiz.length} maç</DaySelectorCount>
                                                        </DaySelectorItem>
                                                    )}
                                                </DaySelectorList>
                                            </React.Fragment>
                                        )
                                    }
                                    const chosenKey = this.state.selectedDateKey
                                    const dayMatches = byUploadDate[chosenKey] || []
                                    return (
                                        <React.Fragment>
                                            <BackButton type="button" onClick={this.clearSelectedDay}>
                                                ← Günlere dön
                                            </BackButton>
                                            <Subtitle style={{ marginBottom: '1rem' }}>
                                                {chosenKey === 'tarihsiz' ? 'Tarihsiz maçlar' : formatMatchDate(chosenKey) + ' — ' + dayMatches.length + ' maç'}
                                            </Subtitle>
                                            <List>
                                                {dayMatches.map(m => (
                                                    <ListItem key={m.id}>
                                                        <MatchLink
                                                            href={`/match/${m.id}`}
                                                            onClick={e => {
                                                                e.preventDefault()
                                                                this.props.history.push(`/match/${m.id}`)
                                                            }}
                                                        >
                                                            <MatchTitle>{getMatchLabel(m)}</MatchTitle>
                                                        </MatchLink>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </React.Fragment>
                                    )
                                })()}
                            </React.Fragment>
                        )}
                    </Section>
                )}

                {!showSupabase && (
                    <Subtitle style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
                        Turnuva maçlarını görmek için sunucu yapılandırması gerekli.
                    </Subtitle>
                )}
            </Page>
        )
    }
}

export default Home




// WEBPACK FOOTER //
// ./src/routes/Home/index.js